import { useMemo, useRef, useState, type ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { List, Ticket, UserProfile } from '@/lib/types'
import { cn, displayListName, listDotClass, LIST_COLORS } from '@/lib/utils'
import { Column } from './Column'
import { TicketCard } from './TicketCard'
import type { AudioSpeed } from './InlineAudio'
import { Plus, Check, X } from 'lucide-react'

interface Props {
  lists: List[]
  tickets: Ticket[]
  projectId: string
  /** True when the current project is the owner's central Inbox — cards get
   *  a prominent « Affecter à un canal » button. */
  isInbox?: boolean
  /** True when a search query is filtering the board — matching cards get highlighted. */
  searchActive: boolean
  profilesById: Record<string, UserProfile>
  /** Per-list compact state — list IDs whose cards render in slim layout. */
  compactListIds: Set<string>
  /** Where a manually-added card lands (user pref) — drives the composer's
   *  position (top vs bottom of the column). */
  newCardPosition?: 'top' | 'bottom'
  /** Toggle compact mode for a specific list (saved in user prefs). */
  onToggleListCompact: (listId: string) => void
  /** All user_ids who can be subscribed to any card in this project
   *  (owner + project members). */
  subscribableUserIds: string[]
  /** Map ticket_id → set of subscribed user_ids. */
  subsByTicket: Map<string, Set<string>>
  /** The current viewer — used to highlight "you" in the picker. */
  currentUserId: string
  /** Subscribe / unsubscribe a user to a ticket (optimistic + email notify). */
  onToggleSubscriber: (
    ticketId: string,
    userId: string,
    subscribe: boolean,
  ) => void | Promise<void>
  /** Cards mid fade-out (archived from the board). */
  exitingIds: Set<string>
  /** Cards mid entrance animation (just inserted, locally or via realtime). */
  enteringIds: Set<string>
  /** Ids of cards currently flagged "new" (unseen by this user, within the
   *  configurable window). */
  newTicketIds: Set<string>
  onUpdateTicket: (id: string, patch: Partial<Ticket>) => void | Promise<void>
  onCreateCard: (
    listId: string,
    title: string,
    description?: string,
  ) => void | Promise<void>
  onArchiveCard: (id: string) => void
  /** Permanently delete a card (kebab « Supprimer ») — deferred with an
   *  undo toast handled by the parent. */
  onDeleteCard?: (id: string) => void
  onMarkSeen?: (id: string) => void
  onCreateList: (
    projectId: string,
    name: string,
    color: string | null,
  ) => void | Promise<void>
  onRenameList: (id: string, name: string) => void | Promise<void>
  onDeleteList: (id: string) => void | Promise<void>
  onSetListColor: (id: string, color: string | null) => void | Promise<void>
  /** Persist a new left-to-right ordering of the project's lists. */
  onReorderLists: (orderedIds: string[]) => void | Promise<void>
  onTicketClick: (ticket: Ticket) => void
  /** Open the « Déplacer la carte » dialog for this ticket (cross-project move). */
  onMoveCard?: (ticket: Ticket) => void
  /** Open the « Répondre » dialog (AI-suggested replies + composer). */
  onReplyCard?: (ticket: Ticket) => void
  onViewMedia: (urls: string[]) => void
  onViewAudio: (urls: string[], ticket: Ticket) => void
  /** Transcribe a single audio URL and rewrite the ticket's title +
   *  description from the result (used by the inline audio player's kebab
   *  menu). */
  onTranscribeAudio?: (
    ticket: Ticket,
    audioUrl: string,
  ) => void | Promise<void>
  /** Tickets whose card-level transcription is currently in flight. The
   *  TicketCard reads this set to show a small « Transcription en cours… »
   *  chip so the user knows the AI is working. */
  transcribingTicketIds?: Set<string>
  /** Global audio playback rate (1×, 1.25×, …, 2×) and its setter — same
   *  value propagated to every InlineAudio so the choice is universal. */
  audioSpeed?: AudioSpeed
  onAudioSpeedChange?: (rate: AudioSpeed) => void
  onHoverChange: (ticket: Ticket | null, rect: DOMRect | null) => void
  /** Signals when a CARD drag starts / ends, so the sidebar can grey out
   *  the channels a card can't be dropped on (real WhatsApp numbers). */
  onCardDragChange?: (active: boolean) => void
  /** Move a card to a (personal) channel — invoked when a card is dropped
   *  onto a droppable channel row in the sidebar. */
  onMoveCardToChannel?: (ticketId: string, projectId: string) => void
  /** Request a merge of two cards — fired when the user drags a card and
   *  dwells ~5s over another card. `draggedId` is the card under the cursor
   *  (the one the user picked up), `targetId` is the card hovered. App
   *  resolves which survives (the older one) and opens the merge modal. */
  onRequestMerge?: (draggedId: string, targetId: string) => void
}

export function KanbanBoard({
  lists,
  tickets,
  projectId,
  isInbox = false,
  searchActive,
  profilesById,
  compactListIds,
  newCardPosition,
  onToggleListCompact,
  subscribableUserIds,
  subsByTicket,
  currentUserId,
  onToggleSubscriber,
  exitingIds,
  enteringIds,
  newTicketIds,
  onUpdateTicket,
  onCreateCard,
  onArchiveCard,
  onDeleteCard,
  onMarkSeen,
  onCreateList,
  onRenameList,
  onDeleteList,
  onSetListColor,
  onReorderLists,
  onTicketClick,
  onMoveCard,
  onReplyCard,
  onViewMedia,
  onViewAudio,
  onTranscribeAudio,
  transcribingTicketIds,
  audioSpeed,
  onAudioSpeedChange,
  onHoverChange,
  onCardDragChange,
  onMoveCardToChannel,
  onRequestMerge,
}: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )
  const [activeId, setActiveId] = useState<string | null>(null)

  // ── Merge-on-drop state ─────────────────────────────────────────────────
  // When the user drags a card and holds it over ANOTHER card for 3s, BOTH
  // cards (the floating drag overlay + the static target) start to vibrate
  // and grow a yellow halo, then keep buzzing for as long as the cards
  // overlap. Moving off the target stops the effect immediately. NOTHING
  // auto-fires: it's the DROP that decides —
  //   • release ON the (charged) target card  → open the merge modal
  //   • release anywhere else (empty slot)     → cancel, normal move/reorder
  // The 3s hold makes the trigger deliberate (no accidental charge while
  // sorting, where the cursor only grazes cards).
  const CHARGE_START_MS = 3000 // overlap hold before the merge effect appears
  // The pair of cards currently charging toward a merge. Both vibrate + grow
  // a yellow halo. `targetId` is the static card, `draggedId` the one at the
  // cursor (rendered in the DragOverlay). Non-null also means a drop ON the
  // target now merges (the effect is showing = merge armed).
  const [chargingPair, setChargingPair] = useState<{
    draggedId: string
    targetId: string
  } | null>(null)
  const chargingRef = useRef(false)
  // Tracks the card the cursor is dwelling over + its pending charge timer.
  const dwellRef = useRef<{ id: string; chargeTimer: number } | null>(null)

  function clearDwell() {
    if (dwellRef.current) {
      window.clearTimeout(dwellRef.current.chargeTimer)
      dwellRef.current = null
    }
  }

  function clearCharging() {
    chargingRef.current = false
    setChargingPair(null)
  }

  function resetMergeState() {
    clearDwell()
    clearCharging()
  }

  // 2s stable overlap → show the charging feedback (vibrate + growing yellow
  // halo) on both cards AND arm the merge (a drop on the target now merges).
  function startCharging(draggedId: string, targetId: string) {
    chargingRef.current = true
    setChargingPair({ draggedId, targetId })
    // The merge intent supersedes any cross-list drop placeholder.
    setCrossListSlot(null)
  }

  // Called on every drag-over: (re)starts the 2s charge timer whenever the
  // cursor settles over a ticket OTHER than the dragged one, and cancels
  // everything the instant the cursor moves to a different card / column /
  // empty space (so the effect stops as soon as the cards stop overlapping).
  function updateMergeDwell(
    over: DragOverEvent['over'],
    activeIdStr: string,
  ) {
    const overTicketId =
      over && over.data.current?.type === 'ticket' ? String(over.id) : null
    const target =
      overTicketId && overTicketId !== activeIdStr ? overTicketId : null
    if (!target) {
      clearDwell()
      clearCharging()
      return
    }
    if (dwellRef.current?.id === target) return // already counting this card
    clearDwell()
    clearCharging()
    dwellRef.current = {
      id: target,
      chargeTimer: window.setTimeout(
        () => startCharging(activeIdStr, target),
        CHARGE_START_MS,
      ),
    }
  }

  // Snapshot of `tickets` taken at drag start. While a drag is in flight
  // we render the board from this snapshot instead of the live prop, so
  // realtime echoes and poll reconciles can't reshuffle the SortableContext
  // items array under dnd-kit's feet. dnd-kit re-measures every droppable
  // when its items array changes; if those measurements update mid-drag,
  // we have seen React error #185 — "Maximum update depth exceeded" —
  // fire from dnd-kit's internal node-rect observer loop. Freezing the
  // input cuts that feedback path.
  const [frozenTickets, setFrozenTickets] = useState<Ticket[] | null>(null)
  const activeTickets = frozenTickets ?? tickets

  // Cross-list-only visual feedback: when the cursor is over a list
  // DIFFERENT from the source list, we want the destination column to
  // make room for the incoming card by rendering a sized placeholder
  // slot at the cursor's slot, AND the source card to disappear (so
  // the "ghost" follows the cursor across columns). Null means same-
  // list drag in progress (or no drag) — the source card stays in
  // place as a translucent ghost and dnd-kit's native sortable handles
  // the visual push of neighbours.
  const [crossListSlot, setCrossListSlot] = useState<{
    listId: string
    index: number
  } | null>(null)

  const byList = useMemo(() => {
    const map: Record<string, Ticket[]> = {}
    for (const l of lists) map[l.id] = []
    for (const t of activeTickets) {
      if (map[t.list_id]) map[t.list_id].push(t)
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => a.position - b.position)
    }
    return map
  }, [lists, activeTickets])

  // Unique labels used anywhere in this project's tickets — surfaced as
  // one-click suggestion chips in each card's contextual menu so the user
  // can reapply an existing label without retyping its name.
  const projectLabels = useMemo(() => {
    const seen = new Set<string>()
    for (const t of activeTickets) {
      for (const l of t.labels ?? []) {
        const v = l.trim()
        if (v) seen.add(v)
      }
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b))
  }, [activeTickets])

  // (Historical note: we used to maintain an `overrides` state that moved
  // the dragged card into the destination column's items array during a
  // cross-list drag so the cards there would visually push apart. But
  // moving a card across SortableContext parents UNMOUNTS and REMOUNTS
  // its DOM node — React preserves identity only within the same parent,
  // even with the same key. dnd-kit's `useRect` observer fires on the new
  // node, calls setRect inside a layout effect, which re-renders, which
  // shifts collision candidates and re-fires `onDragOver`, which would
  // re-apply the override — a feedback loop ending in React error #185.
  // Cross-list dnd works fine without it: the floating DragOverlay shows
  // the cursor preview, and `handleDragEnd` resolves the drop slot from
  // `over.id` directly.)

  // Resolve from the FROZEN tickets so the DragOverlay's content doesn't
  // mutate mid-drag if a realtime echo arrives before the user releases.
  const activeTicket = activeId
    ? activeTickets.find((t) => t.id === activeId) ?? null
    : null
  const activeList = activeId ? lists.find((l) => l.id === activeId) ?? null : null

  // Custom collision detection — pointer-first, with two layers of
  // specificity:
  //   1. If the cursor is literally OVER a ticket (not the active one),
  //      that's our target — most specific.
  //   2. Otherwise if the cursor is inside a column (in the padding above
  //      or below the cards, or inside an empty column), use the column.
  //      This is the fix for "can't drop at the top of a list" and
  //      "can't drop into an empty list": closestCenter would have
  //      picked some card in a completely unrelated column, which then
  //      hijacked targetListId.
  //   3. Last resort, cursor outside any droppable → closest ticket as
  //      a heuristic so a wild drop still lands somewhere.
  //
  // The active card is always excluded so we don't self-match (cause of
  // earlier bugs where over.id === active.id silently dropped the move).
  const collisionDetection: CollisionDetection = (args) => {
    if (args.active.data.current?.type === 'ticket') {
      const activeListId = args.active.data.current?.listId as
        | string
        | undefined
      const within = pointerWithin(args)
      if (within.length > 0) {
        // 1. Pointer directly over a ticket (not the active one) → that
        //    ticket. Most specific, works everywhere.
        const ticketHits = within.filter((c) => {
          if (c.id === args.active.id) return false
          const dc = collisionContainerData(c)
          return dc?.type !== 'column'
        })
        if (ticketHits.length > 0) return ticketHits

        // 2. Pointer over a column (= in its padding, or it's an empty
        //    column). Two sub-cases:
        //    a. Same list as active's source AND that list has other
        //       tickets → fall back to closestCenter on the source's
        //       tickets so dnd-kit's native sortable strategy has an
        //       index to anchor on (overIndex = -1 produces a broken
        //       visual otherwise).
        //    b. Cross-list target (different list, or empty source) →
        //       return the column. handleDragOver / handleDragEnd both
        //       handle column-as-target via cursor midline.
        const columnHits = within.filter((c) => {
          const dc = collisionContainerData(c)
          return dc?.type === 'column'
        })
        if (columnHits.length > 0) {
          const hoveredCol = columnHits[0]
          const sameListInPadding = hoveredCol.id === activeListId
          if (sameListInPadding) {
            const sourceTickets = args.droppableContainers.filter(
              (c) =>
                c.data.current?.type !== 'column' &&
                c.id !== args.active.id &&
                c.data.current?.listId === activeListId,
            )
            if (sourceTickets.length > 0) {
              const hits = closestCenter({
                ...args,
                droppableContainers: sourceTickets,
              })
              if (hits.length > 0) return hits
            }
          }
          return [hoveredCol]
        }
      }
      // Pointer outside every droppable — fall back to closest ticket.
      const ticketContainers = args.droppableContainers.filter(
        (c) => c.data.current?.type !== 'column' && c.id !== args.active.id,
      )
      if (ticketContainers.length > 0) {
        const hits = closestCenter({
          ...args,
          droppableContainers: ticketContainers,
        })
        if (hits.length > 0) return hits
      }
    }
    const pw = pointerWithin(args)
    return pw.length > 0 ? pw : rectIntersection(args)
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id))
    // Freeze the live tickets array so realtime / poll updates can't
    // perturb dnd-kit's measurements while a drag is in flight.
    setFrozenTickets(tickets)
    setCrossListSlot(null)
    resetMergeState()
    onHoverChange(null, null)
    // Card drag (not a list) → let the sidebar light up droppable channels.
    if (event.active.data.current?.type !== 'column') onCardDragChange?.(true)
  }

  // Track cross-list drag state:
  //   - cursor over the SAME list as source → clear crossListSlot.
  //     dnd-kit's native sortable handles the visual push in-place.
  //   - cursor over a DIFFERENT list → set crossListSlot to where the
  //     drop would land in the destination. Column will render a sized
  //     placeholder at that slot (making the cards there spread apart),
  //     and TicketCard will hide the source card (h-0) so the "ghost"
  //     visually follows the cursor across columns.
  function handleDragOver(event: DragOverEvent) {
    const { active, over } = event
    // Track the merge dwell timer for card drags (not list reorders).
    if (active.data.current?.type !== 'column') {
      updateMergeDwell(over, String(active.id))
    }
    // While charging over a target, the merge intent owns the visuals —
    // suppress the cross-list drop placeholder.
    if (chargingRef.current) {
      if (crossListSlot) setCrossListSlot(null)
      return
    }
    if (!over) {
      if (crossListSlot) setCrossListSlot(null)
      return
    }
    if (active.data.current?.type === 'column') return

    const activeIdStr = String(active.id)
    const overId = String(over.id)
    const ticket = activeTickets.find((t) => t.id === activeIdStr)
    if (!ticket) {
      if (crossListSlot) setCrossListSlot(null)
      return
    }

    const targetListId = lists.some((l) => l.id === overId)
      ? overId
      : activeTickets.find((t) => t.id === overId)?.list_id

    // Same list (or unresolvable target) → no cross-list visual.
    if (!targetListId || targetListId === ticket.list_id) {
      if (crossListSlot) setCrossListSlot(null)
      return
    }

    const cursorY = pointerYOf(event)
    if (cursorY === null) return

    // Live DOM measurement of destination column's cards — direct,
    // never stale, and the destination doesn't contain the dragged
    // card so we don't have to filter it out.
    const index = computeCrossListSlot(targetListId, cursorY)
    if (index === null) return

    if (
      crossListSlot &&
      crossListSlot.listId === targetListId &&
      crossListSlot.index === index
    ) {
      return
    }
    setCrossListSlot({ listId: targetListId, index })
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null)
    const { active, over } = event
    const slotAtRelease = crossListSlot
    setCrossListSlot(null)
    setFrozenTickets(null)
    onCardDragChange?.(false)
    clearDwell()

    // The DROP decides the merge. If the merge effect was active (2s hold
    // elapsed, cards vibrating) AND the release lands on a ticket OTHER than
    // the dragged one, open the merge modal. Any other release (on empty
    // space / a column, or before the 2s hold) cancels the merge and falls
    // through to the normal move/reorder — that's how the user backs out:
    // drag off the target onto a free slot and drop there.
    const wasCharging = chargingRef.current
    clearCharging()
    if (
      wasCharging &&
      over &&
      over.data.current?.type === 'ticket' &&
      String(over.id) !== String(active.id)
    ) {
      onRequestMerge?.(String(active.id), String(over.id))
      return
    }

    // ── Drop onto a sidebar channel (cross-context) ─────────────────────
    // The sidebar lives in a different Dnd context, so we hit-test the
    // release point against its DOM (channel rows carry data-channel-drop).
    // Only personal (manual) channels accept a card; real WhatsApp-number
    // channels are inert.
    if (active.data.current?.type !== 'column') {
      const ticket = activeTickets.find((t) => t.id === active.id)
      const pt = pointerXYOf(event)
      if (ticket && pt) {
        const el = document
          .elementFromPoint(pt.x, pt.y)
          ?.closest('[data-channel-drop]') as HTMLElement | null
        if (el) {
          const targetProjectId = el.dataset.channelDrop
          const personal = el.dataset.channelPersonal === '1'
          if (
            personal &&
            targetProjectId &&
            targetProjectId !== ticket.project_id
          ) {
            onMoveCardToChannel?.(ticket.id, targetProjectId)
          }
          // Released over a channel row (droppable or not) → handled here,
          // never fall through to the board move logic.
          return
        }
      }
    }

    if (!over) return

    // ── Column reorder ──────────────────────────────────────────────
    if (active.data.current?.type === 'column') {
      const overId = String(over.id)
      const overListId = lists.some((l) => l.id === overId)
        ? overId
        : activeTickets.find((t) => t.id === overId)?.list_id
      if (!overListId || overListId === active.id) return
      const oldIndex = lists.findIndex((l) => l.id === active.id)
      const newIndex = lists.findIndex((l) => l.id === overListId)
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return
      onReorderLists(arrayMove(lists, oldIndex, newIndex).map((l) => l.id))
      return
    }

    // ── Ticket move ─────────────────────────────────────────────────
    // Read from the FROZEN snapshot so the drop's source-list resolution
    // is stable even if a realtime echo moved the row server-side during
    // the drag.
    const ticket = activeTickets.find((t) => t.id === active.id)
    if (!ticket) return
    const overId = String(over.id)

    // Resolve target list directly from `over.id` at drop time:
    //   - over.id is a column   → that column
    //   - over.id is a ticket   → that ticket's list (read from the frozen
    //                             snapshot for stability)
    //   - anything else         → fall back to the dragged card's own list
    const targetListId = lists.some((l) => l.id === overId)
      ? overId
      : activeTickets.find((t) => t.id === overId)?.list_id ??
        ticket.list_id

    if (targetListId === ticket.list_id) {
      // Same-column: dnd-kit's native sortable was visually pushing the
      // neighbour cards around the cursor during the drag — the user
      // saw exactly where the gap was. Read over.id (= the sibling whose
      // slot the dragged card now occupies) and compute the resulting
      // index via arrayMove, then sandwich the dragged card between its
      // new neighbours' positions.
      const sourceItems = byList[ticket.list_id] ?? []
      const oldIndex = sourceItems.findIndex((t) => t.id === ticket.id)
      if (oldIndex < 0) return

      let newIndex: number
      if (lists.some((l) => l.id === overId)) {
        // Dropped on the column itself (cursor in the padding). Pick top
        // or bottom from the cursor's position relative to the column.
        const overRect = over.rect
        const cursorY = pointerYOf(event)
        const dropAtBottom =
          cursorY !== null &&
          !!overRect &&
          cursorY > overRect.top + overRect.height / 2
        newIndex = dropAtBottom ? sourceItems.length - 1 : 0
      } else {
        newIndex = sourceItems.findIndex((t) => t.id === overId)
      }
      if (newIndex < 0 || newIndex === oldIndex) return

      const reordered = arrayMove(sourceItems, oldIndex, newIndex)
      const before = newIndex > 0 ? reordered[newIndex - 1] : null
      const after =
        newIndex < reordered.length - 1 ? reordered[newIndex + 1] : null
      const position = betweenPositions(before, after)
      onUpdateTicket(ticket.id, { position })
      return
    }

    // Cross-column: use the same slot the placeholder was showing at
    // release time — guaranteed consistent with what the user saw.
    // Fallback to a live measurement if (rare) the placeholder wasn't
    // set, e.g. an instant drop.
    const targetItems = byList[targetListId] ?? []
    let insertIndex: number
    if (slotAtRelease && slotAtRelease.listId === targetListId) {
      insertIndex = slotAtRelease.index
    } else {
      const cursorY = pointerYOf(event)
      insertIndex =
        cursorY !== null
          ? computeCrossListSlot(targetListId, cursorY) ?? targetItems.length
          : targetItems.length
    }
    const before = insertIndex > 0 ? targetItems[insertIndex - 1] : null
    const after =
      insertIndex < targetItems.length ? targetItems[insertIndex] : null
    const position = betweenPositions(before, after)

    const fromDone = lists.find((l) => l.id === ticket.list_id)?.is_done ?? false
    const toDone = lists.find((l) => l.id === targetListId)?.is_done ?? false

    const patch: Partial<Ticket> = { list_id: targetListId, position }
    if (toDone && !fromDone) patch.done_at = new Date().toISOString()
    if (!toDone && fromDone) patch.done_at = null

    onUpdateTicket(ticket.id, patch)
  }

  function handleDragCancel() {
    setActiveId(null)
    setCrossListSlot(null)
    setFrozenTickets(null)
    onCardDragChange?.(false)
    resetMergeState()
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex h-full gap-3 overflow-x-auto p-3">
        <SortableContext
          items={lists.map((l) => l.id)}
          strategy={horizontalListSortingStrategy}
        >
          {lists.map((l) => (
            <SortableColumn
              key={l.id}
              list={l}
              allLists={lists}
              tickets={byList[l.id] ?? []}
              searchActive={searchActive}
              profilesById={profilesById}
              exitingIds={exitingIds}
              enteringIds={enteringIds}
              newTicketIds={newTicketIds}
              onTicketClick={onTicketClick}
              isInbox={isInbox}
              onMoveCard={onMoveCard}
              onReplyCard={onReplyCard}
              onViewMedia={onViewMedia}
              onViewAudio={onViewAudio}
              onTranscribeAudio={onTranscribeAudio}
              transcribingTicketIds={transcribingTicketIds}
              audioSpeed={audioSpeed}
              onAudioSpeedChange={onAudioSpeedChange}
              onHoverChange={onHoverChange}
              onArchiveCard={onArchiveCard}
              onDeleteCard={onDeleteCard}
              onMarkSeen={onMarkSeen}
              onUpdateTicket={onUpdateTicket}
              compact={compactListIds.has(l.id)}
              onToggleCompact={() => onToggleListCompact(l.id)}
              subscribableUserIds={subscribableUserIds}
              subsByTicket={subsByTicket}
              currentUserId={currentUserId}
              onToggleSubscriber={onToggleSubscriber}
              onCreateCard={onCreateCard}
              onRename={onRenameList}
              onDelete={onDeleteList}
              onSetColor={onSetListColor}
              dropPlaceholderIndex={
                crossListSlot?.listId === l.id ? crossListSlot.index : null
              }
              activeDragTicketId={activeId}
              crossListActive={crossListSlot !== null}
              mergeTargetId={chargingPair?.targetId ?? null}
              newCardPosition={newCardPosition}
              projectLabels={projectLabels}
            />
          ))}
        </SortableContext>
        <div className="w-[280px] shrink-0">
          <AddListButton
            onAdd={(name, color) => onCreateList(projectId, name, color)}
          />
        </div>
      </div>
      <DragOverlay dropAnimation={null} style={{ pointerEvents: 'none' }}>
        {activeTicket ? (
          <TicketCard
            ticket={activeTicket}
            dragging
            merging={chargingPair !== null}
            assigneeProfile={
              activeTicket.assignee_id
                ? profilesById[activeTicket.assignee_id]
                : undefined
            }
          />
        ) : activeList ? (
          <ColumnDragPreview
            list={activeList}
            count={(byList[activeList.id] ?? []).length}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

/** Extract the droppable container's metadata `data.current` (= what we
 *  set via useSortable's `data` option) from a Collision result. dnd-kit
 *  attaches the full container under `collision.data.droppableContainer`
 *  for both pointerWithin and closestCenter — type info is loose, so we
 *  go through a tiny shape we control. */
type CollisionContainerData = { type?: string; listId?: string }
function collisionContainerData(
  c: { data?: unknown },
): CollisionContainerData | undefined {
  const d = c.data as
    | { droppableContainer?: { data?: { current?: CollisionContainerData } } }
    | undefined
  return d?.droppableContainer?.data?.current
}

/** For a cross-list drag, compute the insertion index in the target
 *  column from the cursor's Y coordinate. Uses LIVE bounding rects so
 *  it stays accurate even right after the source card collapses (the
 *  reflow doesn't fire any ResizeObserver that dnd-kit would catch).
 *
 *  Returns the index in `byList[targetListId]` (which doesn't contain
 *  the dragged card — cross-list), or `null` if the destination column
 *  isn't mounted yet. An empty column yields 0. */
function computeCrossListSlot(
  targetListId: string,
  cursorY: number,
): number | null {
  const body = document.querySelector(
    `[data-list-body="${cssEscape(targetListId)}"]`,
  )
  if (!body) return null

  const cardEls = body.querySelectorAll<HTMLElement>('[data-ticket-id]')
  for (let i = 0; i < cardEls.length; i++) {
    const rect = cardEls[i].getBoundingClientRect()
    if (cursorY < rect.top + rect.height / 2) return i
  }
  return cardEls.length
}

/** Browser CSS.escape via window — dnd-kit's `CSS` import shadows the
 *  global at module scope, so we go through `window.CSS` to be safe.
 *  Falls back to a minimal manual escape if unavailable. */
function cssEscape(s: string): string {
  const w = typeof window !== 'undefined' ? window : undefined
  const browserCss = w?.CSS
  return browserCss && typeof browserCss.escape === 'function'
    ? browserCss.escape(s)
    : s.replace(/["\\]/g, '\\$&')
}

/** Current pointer Y at the moment of the drag event, computed from the
 *  activator event (initial mousedown/pointerdown) + accumulated delta.
 *  Used for midline checks instead of the dragged card's centre — the
 *  user grabs the card wherever they please (often the top), so its
 *  centre is meaningfully different from where the cursor actually is.
 *  Returns null for keyboard activators (no Y coordinate available). */
function pointerYOf(event: DragOverEvent | DragEndEvent): number | null {
  const e = event.activatorEvent as Partial<{ clientY: number }>
  if (typeof e.clientY !== 'number') return null
  return e.clientY + event.delta.y
}

/** Release point (viewport coords) of a drag — activator position plus the
 *  accumulated delta. Used to hit-test the sidebar DOM for channel drops. */
function pointerXYOf(event: DragEndEvent): { x: number; y: number } | null {
  const e = event.activatorEvent as Partial<{ clientX: number; clientY: number }>
  if (typeof e.clientX !== 'number' || typeof e.clientY !== 'number') return null
  return { x: e.clientX + event.delta.x, y: e.clientY + event.delta.y }
}

/** Compute a dense float `position` slotted between two neighbours. Used by
 *  the drag-end handler so a dropped card lands EXACTLY at the previewed
 *  slot (between the cards above and below it) instead of always becoming
 *  the top of the column. */
function betweenPositions(
  before: { position: number } | null,
  after: { position: number } | null,
): number {
  if (before && after) return (before.position + after.position) / 2
  if (before) return before.position + 1
  if (after) return after.position - 1
  return 0
}

/** Wraps a Column in a horizontal sortable so whole lists can be reordered. */
function SortableColumn(
  props: Omit<ComponentProps<typeof Column>, 'dragHandleProps' | 'isOver'>,
) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
    isOver,
    active,
  } = useSortable({ id: props.list.id, data: { type: 'column' } })
  // Only light up the dropzone for card drags, not when another list passes over.
  const dropActive = isOver && active?.data.current?.type === 'ticket'
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn('flex h-full w-[300px] shrink-0', isDragging && 'z-10 opacity-40')}
    >
      <Column
        {...props}
        isOver={dropActive}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  )
}

/** Static column placeholder shown in the DragOverlay while a list is dragged. */
function ColumnDragPreview({ list, count }: { list: List; count: number }) {
  const dot = listDotClass(list)
  return (
    <div className="w-[300px] rotate-2 rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface-2)] px-4 py-3 shadow-2xl ring-2 ring-[var(--color-accent)]/30">
      <div className="flex items-center gap-2">
        <span className={cn('h-2 w-2 shrink-0 rounded-full', dot)} />
        <span className="truncate text-[13px] font-semibold uppercase tracking-wide text-[var(--color-text-strong)]">
          {displayListName(list)}
        </span>
        <span className="tabular-nums text-[12px] text-[var(--color-text-dim)]">
          {count}
        </span>
      </div>
    </div>
  )
}

/** "+ Ajouter une liste" — toggles to an inline input with a color picker. */
function AddListButton({
  onAdd,
}: {
  onAdd: (name: string, color: string | null) => void
}) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [color, setColor] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  function commit() {
    const trimmed = name.trim()
    if (trimmed) onAdd(trimmed, color)
    setName('')
    setColor(null)
    setEditing(false)
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setEditing(true)
          setTimeout(() => inputRef.current?.focus(), 0)
        }}
        className="flex w-full items-center gap-2 rounded-3xl border border-dashed border-[var(--color-border)] px-4 py-3 text-[13px] font-medium text-[var(--color-text-dim)] transition-colors hover:border-[var(--color-accent)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
      >
        <Plus className="h-4 w-4" />
        {t('board.addList')}
      </button>
    )
  }

  return (
    <div className="rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface-2)] p-2.5 shadow-[var(--shadow-soft)]">
      <input
        ref={inputRef}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') {
            setName('')
            setEditing(false)
          }
        }}
        placeholder={t('board.listNamePlaceholder')}
        className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none focus:border-[var(--color-link)]"
      />
      {/* 12-colour rainbow as a 6×2 grid — same layout as the list /
          project pickers for visual consistency. Selected swatch shows
          a white check inside, no ring/offset (avoids the white halo
          rubbing against neighbours). */}
      <div className="mt-2 grid grid-cols-6 gap-1.5 px-0.5">
        {LIST_COLORS.map((c) => {
          const selected = color === c.key
          return (
            <button
              key={c.key}
              type="button"
              title={c.label}
              aria-pressed={selected}
              onClick={() => setColor(selected ? null : c.key)}
              className={cn(
                'relative inline-flex h-5 w-5 items-center justify-center rounded-full text-white transition-transform hover:z-10 hover:scale-110',
                c.dot,
              )}
            >
              {selected && <Check className="h-3 w-3" strokeWidth={3} />}
            </button>
          )
        })}
      </div>
      <div className="mt-2 flex gap-1.5">
        <button
          type="button"
          onClick={commit}
          className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-accent)] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[var(--color-accent-hover)]"
        >
          <Check className="h-3.5 w-3.5" />
          {t('common.add')}
        </button>
        <button
          type="button"
          onClick={() => {
            setName('')
            setEditing(false)
          }}
          className="rounded-lg p-1.5 text-[var(--color-text-dim)] hover:bg-[var(--color-surface-3)]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
